import { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Clapperboard } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import Seo from "@/components/Seo";
import { useDailyCall } from "@/components/studio/useDailyCall";
import { ParticipantTile } from "@/components/studio/ParticipantTile";
import { StudioControls } from "@/components/studio/StudioControls";
import { StudioHostBar } from "@/components/studio/StudioHostBar";
import { BrandPreview } from "@/components/studio/StudioOverlayPanel";

function Stage({ isHost, joinInfo, onLeave, info }) {
  const { callRef, participants, state, error } = useDailyCall(joinInfo);
  const list = Object.values(participants);
  const screens = list.filter((p) => p.tracks?.screenVideo?.state === "playable");
  const call = callRef.current;
  const mute = (p) => { call?.updateParticipant(p.session_id, { setAudio: false }); toast.success(`${p.user_name} muted`); };
  const remove = (p) => { if (window.confirm(`Remove ${p.user_name} from the studio?`)) call?.updateParticipant(p.session_id, { eject: true }); };
  if (state === "error") return <div className="eyw-card p-8 text-center text-red-700" data-testid="studio-error">{error}</div>;
  if (state !== "joined") return <div className="aspect-video rounded-2xl bg-[#1A242B] grid place-items-center text-white/70" data-testid="studio-joining"><Loader2 className="animate-spin" /></div>;
  return (
    <div className="space-y-4" data-testid="studio-stage">
      {error && <p className="text-sm text-[#B5794E]" data-testid="studio-device-warning">{error}</p>}
      {screens.map((p) => <ParticipantTile key={`${p.session_id}-s`} p={p} screen isHost={false} />)}
      <div className="relative">
        <div className={`grid gap-3 ${list.length > 1 ? "sm:grid-cols-2" : ""}`}>
          {list.map((p) => <ParticipantTile key={p.session_id} p={p} isHost={isHost} onMute={mute} onRemove={remove} />)}
        </div>
        {isHost && <BrandPreview info={info} />}
      </div>
      <StudioControls call={call} local={participants.local} onLeave={onLeave} />
    </div>
  );
}

export default function EventStudio() {
  const { id } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const key = ["studio", id];
  const { data: info, isLoading, error } = useQuery({ queryKey: key, queryFn: () => api.get(`/events/${id}/studio`).then((r) => r.data), retry: false, refetchInterval: 15000 });
  const [joinInfo, setJoinInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const refresh = () => qc.invalidateQueries({ queryKey: key });
  const isLive = info?.status === "live" && info?.role === "host";
  const { data: health } = useQuery({ queryKey: ["studio-health", id], enabled: !!isLive, refetchInterval: 10000,
    queryFn: () => api.get(`/events/${info.event.id}/studio/health`).then((r) => r.data) });

  const enter = async () => {
    setBusy(true);
    try {
      if (info.role === "host" && !info.exists) await api.post(`/events/${info.event.id}/studio`);
      const r = await api.post(`/events/${info.event.id}/studio/token`);
      setJoinInfo({ url: r.data.url, token: r.data.token });
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't open the studio"); }
    finally { setBusy(false); }
  };

  if (isLoading) return <div className="min-h-[60vh] grid place-items-center" data-testid="studio-loading"><Loader2 className="animate-spin text-[#144F75]" /></div>;
  if (error || !info) return (
    <div className="min-h-[60vh] grid place-items-center text-center px-6" data-testid="studio-denied">
      <div><h1 className="font-serif text-3xl text-[#1A242B] mb-2">Studio unavailable</h1>
        <p className="text-[#4A5D6B]">{formatApiError(error?.response?.data?.detail) || "You haven't been invited to this studio."}</p>
        <Link to="/events" className="eyw-btn !py-2 text-sm mt-5 inline-flex">Browse events</Link></div>
    </div>
  );
  const watchPath = `/events/${info.event.slug || info.event.id}/watch`;
  const isHost = info.role === "host";
  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-10 overflow-x-hidden" data-testid="event-studio-page">
      <Seo title={`Studio: ${info.event.title}`} path={`/events/${info.event.slug || info.event.id}/studio`} />
      <Link to={watchPath} className="text-sm text-[#144F75] inline-flex items-center gap-1 mb-5 hover:underline" data-testid="studio-back"><ArrowLeft size={14} /> Watch page</Link>
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <span className={`text-xs font-semibold px-3 py-1 rounded-full ${info.status === "live" && (info.role !== "host" || health?.confirmed) ? "bg-red-600 text-white" : "bg-[#144F75]/10 text-[#144F75]"}`} data-testid="studio-status">
          {info.status === "live" ? (info.role !== "host" || health?.confirmed ? "LIVE ON YOUTUBE" : "Connecting to YouTube…") : info.status === "ended" ? "Broadcast ended" : "Backstage"}
        </span>
        <h1 className="font-serif text-3xl md:text-4xl text-[#1A242B]" data-testid="studio-title">{info.event.title}</h1>
      </div>
      <div className={`grid gap-6 ${isHost ? "lg:grid-cols-[minmax(0,1fr)_340px]" : ""}`}>
        {joinInfo ? (
          <Stage info={info} isHost={isHost} joinInfo={joinInfo} onLeave={() => { setJoinInfo(null); nav(watchPath); }} />
        ) : (
          <div className="relative min-h-[300px] sm:min-h-0 sm:aspect-video rounded-2xl bg-[#144F75] text-white grid place-items-center text-center px-6 py-10" data-testid="studio-lobby">
            {isHost && <BrandPreview info={info} />}
            <div className="min-w-0 max-w-full">
              <Clapperboard className="mx-auto mb-3" size={32} />
              <p className="font-serif text-2xl">{isHost ? "Ready when you are." : "You're invited backstage."}</p>
              <p className="text-white/80 mt-2 max-w-md">Your browser will ask to use your camera and microphone. Nothing is broadcast until the host goes live.</p>
              {!info.configured ? <p className="mt-5 text-sm" data-testid="studio-not-configured">The studio isn't set up yet.</p> : (
                <button onClick={enter} disabled={busy || (!isHost && !info.exists)} data-testid="studio-enter" className="eyw-btn !bg-white !text-[#144F75] mt-5 inline-flex disabled:opacity-60">
                  {busy ? "Opening…" : !isHost && !info.exists ? "Waiting for the host to open the studio" : "Enter backstage"}
                </button>
              )}
            </div>
          </div>
        )}
        {isHost && <StudioHostBar eid={info.event.id} info={info} refresh={refresh} watchPath={watchPath} health={health} />}
      </div>
    </div>
  );
}
